import React, { Component } from 'react'
import PropTypes from 'prop-types'
import getCaretCoordinates from 'textarea-caret'
import { isAndroid, isMobile } from 'react-device-detect'

function getHookObject(type, element, startPoint) {
  const caret = getCaretCoordinates(element, element.selectionEnd)

  const result = {
    hookType: type,
    cursor: {
      selectionStart: element.selectionStart,
      selectionEnd: element.selectionEnd,
      top: caret.top,
      left: caret.left,
      height: caret.height,
    },
  }

  if (!startPoint) {
    return result
  }

  result.text = element.value.substr(startPoint, element.selectionStart - startPoint)

  return result
}

class InputTrigger extends Component {
  constructor(args) {
    super(args)

    this.state = {
      triggered: false,
      triggerStartPosition: null,
    }

    this.element = this.props.elementRef
  }

  componentDidMount() {
    this.props.endTrigger(this.resetState)
  }

  handleTriggerUniversal = event => {
    if (isAndroid) {
      const { onStart, onCancel, onType, mobileTrigger } = this.props
      const { which, keyCode } = event

      let code = which || keyCode

      if (code && code !== 229) {
        return
      }

      const selectionStart = this.element.selectionStart - 1
      const { triggered, triggerStartPosition } = this.state
      if (!triggered) {
        const char = this.element.value.substr(selectionStart || 0, 1)

        code = char.charCodeAt(0)

        if (code === mobileTrigger.keyCode) {
          this.setState(
            {
              triggered: true,
              triggerStartPosition: selectionStart + 1,
            },
            () => {
              setTimeout(() => {
                onStart(getHookObject('start', this.element))
              }, 0)
            }
          )
          return null
        }
      } else {
        if (which === 8 || selectionStart < triggerStartPosition) {
          this.setState(
            {
              triggered: false,
              triggerStartPosition: null,
            },
            () => {
              setTimeout(() => {
                onCancel(getHookObject('cancel', this.element))
              }, 0)
            }
          )

          return null
        }

        setTimeout(() => {
          onType(getHookObject('typing', this.element, triggerStartPosition))
        }, 0)
      }
    } else {
      this.handleTrigger(event)
    }
  }

  handleTrigger = event => {
    const { trigger, onStart, onCancel, onType } = this.props

    const { which, keyCode } = event

    const code = which || keyCode

    if (!code || code === 229) {
      return
    }
    const { selectionStart } = event.target
    const lastSymbol = event.target.value.slice(selectionStart - 1, selectionStart)
    const charCode = lastSymbol.charCodeAt(0)
    const { triggered, triggerStartPosition } = this.state

    // for mobile shift should be false, replaced shiftKey with which because shiftKey works randomly during fast typing
    const isShiftKeyPressed = isMobile ? true : which === 16

    if (!triggered) {
      // charCode = 34 is "
      // charCode = 64 is @
      if (trigger.charCode === charCode && isShiftKeyPressed) {
        this.setState(
          {
            triggered: true,
            triggerStartPosition: selectionStart,
          },
          () => {
            setTimeout(() => {
              onStart(getHookObject('start', this.element))
            }, 0)
          }
        )
        return null
      }
    } else {
      if (which === 8 && selectionStart < triggerStartPosition) {
        this.setState(
          {
            triggered: false,
            triggerStartPosition: null,
          },
          () => {
            setTimeout(() => {
              onCancel(getHookObject('cancel', this.element))
            }, 0)
          }
        )
        return null
      }
      setTimeout(() => {
        onType(getHookObject('typing', this.element, triggerStartPosition))
      }, 0)
    }
    return null
  }

  resetState = () => {
    this.setState({
      triggered: false,
      triggerStartPosition: null,
    })
  }

  render() {
    const { elementRef, children, trigger, mobileTrigger, onStart, onCancel, onType, endTrigger, ...rest } = this.props

    return (
      <div role="textbox" tabIndex={-1} onKeyUp={this.handleTriggerUniversal} {...rest}>
        {!elementRef
          ? React.Children.map(this.props.children, child =>
              React.cloneElement(child, {
                ref: element => {
                  this.element = element
                  if (typeof child.ref === 'function') {
                    child.ref(element)
                  }
                },
              })
            )
          : children}
      </div>
    )
  }
}

InputTrigger.propTypes = {
  trigger: PropTypes.shape({
    keyCode: PropTypes.number,
    shiftKey: PropTypes.bool,
    ctrlKey: PropTypes.bool,
    metaKey: PropTypes.bool,
  }),
  mobileTrigger: PropTypes.shape({
    keyCode: PropTypes.number,
  }),
  onStart: PropTypes.func,
  onCancel: PropTypes.func,
  onType: PropTypes.func,
  endTrigger: PropTypes.func,
  children: PropTypes.element.isRequired,
  elementRef: PropTypes.element,
}

InputTrigger.defaultProps = {
  trigger: {
    keyCode: null,
    shiftKey: false,
    ctrlKey: false,
    metaKey: false,
  },
  mobileTrigger: {
    keyCode: null,
  },
  onStart: () => {},
  onCancel: () => {},
  onType: () => {},
  endTrigger: () => {},
  elementRef: null,
}

export default InputTrigger
